import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import _ from 'lodash';

import { Col, Row, Divider, Drawer, notification } from 'antd';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';

import { RootState } from '../../../../application/store';
import { CustomerFacility, OrderProduct, ProductSize } from '../../../../api-client';
import { BUSINESS_UNIT_TYPES } from '../../../../utils/constants/business-unit-types';
import { formatCurrency } from '../../../../utils/data-format-enchanters/currency-enchanter';
import { formatStandardDate, momentFormat } from '../../../../utils/data-format-enchanters/datetime-enchanter';
import { generateProgressiveValue, orderTotalBySizes } from '../../../../utils/data-format-enchanters/order-products-enchanter';
import {
    hasConfiguredNumbers,
    isPoolLoaded,
    isSelectionComplete,
    numbersTakenElsewhereInCart,
    selectableNumbers,
    trimSelectionToQuantity,
} from '../../../../utils/limited-edition-utils';
import { sapCodeOfSize } from '../../../../utils/quota-utils';
import { keepProduct, deleteProduct } from '../../../../slices/OrderSummary/orderSummarySlice';
import { useOrder } from '../../../hooks/useOrder';
import { useLimitedEditions } from '../../../hooks/useLimitedEditions';
import { useProduct } from '../../../hooks/useProduct';
import { IProductForm, useProductForm } from './hooks/useProductForm';

import './wide-form-drawer.scss';

import WideFormFooter from './components/WideFormFooter/wide-form-footer';
import BVShimmer from '../../BVShimmers/BVShimmer/bv-shimmer';
import BVComboBox from '../../BVControls/BVComboBox/bv-combo-box';
import BVDatePicker from '../../BVControls/BVDatePicker/bv-date-picker';
import BVInput from '../../BVControls/BVInput/bv-input';
import BVProductPrice from '../../BVProductPrice/bv-product-price';
import BVInputNumber from '../../BVControls/BVInputNumber/bv-input-number';
import BVLimitedEditionSelect from '../../BVControls/BVLimitedEditionSelect/bv-limited-edition-select';

interface IWideFormDrawer {
    drawerVisible: boolean;
    productId?: number;
    orderLineId?: number;
    onDispose: () => void;
}

const highEndTester = /HE Jewelry Watches|HE Complicated Watches/;

const WideFormDrawer: React.FunctionComponent<IWideFormDrawer> = ({ drawerVisible, productId, orderLineId, onDispose }) => {
    const {
        autosaveOrderForCurrentCustomer,
        upsertOrderLoading
    } = useOrder();

    const {
        handleProductsDetail,
        productsDetail,
        productsDetailLoading,
        handleProductSizes,
        productSizes,
        productSizesLoading,
        handleFacilities,
        facilities,
        facilitiesLoading,
        handleAvailableDate,
        availableDate,
        availableDateLoading
    } = useProduct();

    const {
        handleInitialize,
        handleChange,
        handleChangeMultiple,
        formValues
    } = useProductForm();

    const {
        handleLimitedEditions,
        numbersForSapCode,
        limitedEditionsLoading
    } = useLimitedEditions();

    const bvCustomer = useSelector((state: RootState) => state.customer);

    useEffect(() => {
        if (productId && bvCustomer.id) {
            handleProductsDetail(productId);
            handleProductSizes(productId, bvCustomer.id);
            handleFacilities(bvCustomer.id);
        }
    }, [productId]);

    const _handleInitializeForm = () => {
        if (productSizes && facilities && facilities.length > 0) {
            handleInitialize({
                facility: facilities[0],
                sizes: _.map(productSizes, dataItem => ({ ...dataItem, quantity: 0 })) || [],
                subTotal: 0
            });
        }
    }

    useEffect(() => {
        _handleInitializeForm();
    }, [productSizes, facilities]);

    useEffect(() => {
        if (productId && formValues.facility) {
            handleAvailableDate(productId, formValues.facility.id);
        }
    }, [formValues.facility]);

    const bvOrderSummary = useSelector((state: RootState) => state.orderSummary);

    const sapCodesOfProduct = (productSizes ?? [])
        .map(size => sapCodeOfSize(size))
        .filter((sapCode): sapCode is string => !!sapCode);

    // The order being edited is left out of the pool filter, so a row reopened still shows the numbers it is
    // already holding instead of an empty list.
    useEffect(() => {
        if (!productsDetail?.isLimitedEdition || sapCodesOfProduct.length === 0) return;

        handleLimitedEditions(productId, sapCodesOfProduct, bvOrderSummary.id ?? undefined);
    }, [productId, productSizesLoading, productsDetail?.isLimitedEdition]);

    const _handleExistingOrderItem = () => {
        if (!orderLineId) {
            return null;
        }

        return _.find(bvOrderSummary.products, entry => entry.rowId === orderLineId);
    }

    useEffect(() => {
        let existingItem = _handleExistingOrderItem();
        if (existingItem && existingItem.sizes) {
            let formInitializer: IProductForm = {
                facility: existingItem.facility,
                limitedEditionCode: existingItem.limitedEditionCode,
                // The sizes come from the cart with their own limitedEditionCodes, so the selector opens on
                // what was already picked.
                sizes: existingItem.sizes,
                subTotal: orderTotalBySizes(existingItem.sizes).amount
            }

            if (existingItem.deliveryDate) {
                formInitializer.deliveryDate = moment(formatStandardDate(existingItem.deliveryDate), momentFormat);
            }

            handleInitialize(formInitializer);
        }
    }, [orderLineId, productSizesLoading, facilitiesLoading]);

    const _handleFacilityAvailableDate = () => {
        if (!formValues.facility || !availableDate) {
            return '';
        }

        return `Available for ${formValues.facility.name} from ${formatStandardDate(availableDate)}`;
    }

    const _handleFacilityExistingDates = () => {
        if (!formValues.facility) {
            return null;
        }

        let existingOrderProducts = _.filter(bvOrderSummary.products, entry => entry.facility?.id === formValues.facility?.id);
        if (existingOrderProducts.length === 0) {
            return null;
        }

        let existingDates = _.uniqBy(_.map(existingOrderProducts, dataItem => dataItem.deliveryDate), entry => entry);

        return (
            _.sortBy(existingDates, entry => entry).map((existingDate, dateIndex) => (
                existingDate && moment(existingDate) > moment(availableDate).subtract(1, 'days').endOf('day') &&
                <span
                    key={dateIndex}
                    className="shipping-existing-dates"
                    onClick={() => handleChange('deliveryDate', moment(formatStandardDate(existingDate), momentFormat))}
                >
                    {formatStandardDate(existingDate)}
                </span>
            ))
        );
    }

    const dispatch = useDispatch();

    const _handleSubmitProduct = () => {
        let existingItem = _handleExistingOrderItem();
        let orderProduct: OrderProduct = {
            id: productId,
            rowId: (existingItem && existingItem.rowId) || generateProgressiveValue(bvOrderSummary.products),
            businessUnitCode: productsDetail?.businessUnitCode,
            catalogCode: productsDetail?.catalogCode,
            imageUrl: productsDetail?.image && productsDetail.image.galleryUrl,
            name: productsDetail?.descrShort,
            family: productsDetail?.family,
            // Form-Properties
            facility: formValues.facility,
            limitedEditionCode: formValues.limitedEditionCode,
            sizes: formValues.sizes
        };

        if (formValues.deliveryDate) {
            orderProduct.deliveryDate = new Date(formValues.deliveryDate.startOf('day')).toISOString();
        }

        dispatch(keepProduct(orderProduct));

        notification.success({
            message: 'Request successed!',
            description: 'Product successfully saved.',
            duration: 2
        });

        _handleInitializeForm();

        onDispose();

        autosaveOrderForCurrentCustomer();
    }

    const _handleDeleteProduct = (rowId: number) => {
        let existingItem = _.find(bvOrderSummary.products, entry => entry.rowId === rowId);
        if (existingItem) {
            dispatch(deleteProduct(rowId));

            notification.success({
                message: 'Request successed!',
                description: 'Product successfully deleted.',
                duration: 2
            });

            _handleInitializeForm();

            onDispose();
        }
    }

    const _handleSizesChange = (dataItem: ProductSize, value: number) => {
        const normalizedQuantity = value ? value : 0;

        // Spreading dataItem alone would drop the numbers already picked: dataItem is the size as it comes
        // from the catalogue. Lowering the quantity gives the numbers in excess back.
        let editedSizes = formValues.sizes.map(entryItem => (
            entryItem.sapCode === dataItem.sapCode
                ? {
                    ...dataItem,
                    quantity: normalizedQuantity,
                    limitedEditionCodes: trimSelectionToQuantity(
                        entryItem.limitedEditionCodes,
                        normalizedQuantity
                    )
                }
                : entryItem
        ));

        handleChangeMultiple({
            sizes: editedSizes,
            subTotal: orderTotalBySizes(editedSizes).amount
        });
    }

    const isEcc = () => {
        return highEndTester.test(productsDetail?.business || '') ? bvCustomer.isEccHighEnd : bvCustomer.isEcc;
    }

    const _handleSizeHasPool = (dataItem: ProductSize) =>
        hasConfiguredNumbers(numbersForSapCode(sapCodeOfSize(dataItem)));

    /** The numbers a size can offer, minus the ones the other rows of the cart are holding. */
    const _handleNumbersForSize = (dataItem: ProductSize) => {
        const sapCode = sapCodeOfSize(dataItem);

        return selectableNumbers(
            numbersForSapCode(sapCode),
            numbersTakenElsewhereInCart(bvOrderSummary.products, sapCode, orderLineId)
        );
    }

    const _handleCodesChange = (dataItem: ProductSize, codes: string[]) => {
        const editedSizes = formValues.sizes.map(entryItem => (
            sapCodeOfSize(entryItem) === sapCodeOfSize(dataItem)
                ? { ...entryItem, limitedEditionCodes: codes }
                : entryItem
        ));

        handleChange('sizes', editedSizes);
    }

    /**
     * A row cannot be saved while a size with a pool has fewer numbers than pieces, nor while the pool of a
     * limited edition product is still unknown: in that state the form cannot tell whether a number is
     * required at all.
     */
    const _handleIncompleteSelection = () =>
        formValues.sizes.some(size => {
            const pool = numbersForSapCode(sapCodeOfSize(size));

            if (productsDetail?.isLimitedEdition && (size.quantity ?? 0) > 0 && !isPoolLoaded(pool))
                return true;

            return !isSelectionComplete(size.quantity, size.limitedEditionCodes, pool);
        });

    return (
        <Drawer
            className="wide-form-drawer"
            width={750}
            placement="right"
            closable={true}
            onClose={() => {
                _handleInitializeForm();
                onDispose();
            }}
            visible={drawerVisible}
            footer={<WideFormFooter
                disabled={productsDetailLoading
                    || productSizesLoading
                    || facilitiesLoading
                    || availableDateLoading
                    || !formValues.facility
                    || formValues.sizes.every(entry => (entry.quantity || 0) === 0)
                    || _handleIncompleteSelection()
                    || upsertOrderLoading
                }
                orderLineId={orderLineId}
                subTotal={formValues.sizes}
                onDelete={_handleDeleteProduct}
                onSubmit={_handleSubmitProduct}
            />}
        >
            <div className="wide-form-drawer-container">
                <Row>
                    <Col span={6}>
                        <BVShimmer loading={productsDetailLoading} type={'shimmer-image'} style={{ width: '100%' }}>
                            <img src={productsDetail?.image?.galleryUrl} className="products-image" alt={productsDetail?.descrShort} />
                        </BVShimmer>
                    </Col>
                    <Col span={18}>
                        <div className="products-detail-body">
                            <div className="detail-wrapper">
                                <BVShimmer loading={productsDetailLoading} type={'shimmer-details'} style={{ width: '100%' }}>
                                    <React.Fragment>
                                        <span className="heading">
                                            <span>{productsDetail?.descrShort}</span>

                                            <BVShimmer loading={productSizesLoading} type={'shimmer-price'}>
                                                <BVProductPrice
                                                    className="bv-product-form-price"
                                                    sizes={productSizes}
                                                />
                                            </BVShimmer>
                                        </span>
                                        <span className="secondary-heading">
                                            <BVShimmer loading={productSizesLoading} type={'shimmer-details'}>
                                                {productsDetail?.businessUnitCode === BUSINESS_UNIT_TYPES.JEWELRY_CODE &&
                                                    <span>
                                                        {`Cat Code : ${productsDetail?.catalogCode}`}
                                                    </span>
                                                }
                                                {productSizes && productSizes.length === 1 &&
                                                    <span>
                                                        {`Sap Code : ${productSizes[0].sapCode}`}
                                                    </span>
                                                }
                                            </BVShimmer>
                                        </span>
                                        <span className="text">
                                            {productsDetail?.descrShortMarketing}
                                        </span>
                                    </React.Fragment>
                                </BVShimmer>

                                <BVComboBox
                                    label={'Ship to:'}
                                    data={(facilities && facilities.filter((facility) => facility.ecc === isEcc()).map((dataItem) => ({
                                        label: `${dataItem.name} - ${dataItem.address}, ${dataItem.city}`,
                                        value: dataItem.id
                                    }))) || []}
                                    className="facility-combobox"
                                    properties={{
                                        listHeight: 200,
                                        placeholder: 'Select a location...',
                                        disabled: facilitiesLoading,
                                        loading: facilitiesLoading,
                                        value: formValues.facility?.id,
                                        onChange: (value: string) => {
                                            const facilityEntry = facilities?.find((entry: CustomerFacility) => (
                                                entry.id === parseInt(value)
                                            ));

                                            handleChangeMultiple({
                                                facility: facilityEntry,
                                                deliveryDate: null
                                            });
                                        }
                                    }}
                                />

                                {productsDetail && productsDetail.isLimitedEdition &&
                                    !productSizes?.some(dataItem => _handleSizeHasPool(dataItem)) &&
                                    <BVInput
                                        label={'Limited Edition Number:'}
                                        properties={{
                                            placeholder: 'Limited edition Number...',
                                            value: formValues.limitedEditionCode,
                                            onChange: (e: any) => handleChange('limitedEditionCode', e.target.value)
                                        }}
                                    />
                                }
                            </div>
                        </div>
                    </Col>
                </Row>
                <Divider />
                <div className="products-form-group">
                    <span className="heading">
                        {`Choose ${productSizes && productSizes.length > 1 ? 'Sizes' : 'Quantity'}`}
                    </span>
                    <div className="products-form-container">
                        <Row gutter={[16, 16]}>
                            <BVShimmer loading={productSizesLoading} type={'shimmer-details'} style={{ width: '100%' }}>
                                {productSizes && productSizes.map((dataItem, dataIndex) => (
                                    <Col key={dataIndex} span={productSizes.length > 1 ? 6 : 24}>
                                        <BVInputNumber
                                            className={productSizes.length > 1 ? 'bv-size-control' : ''}
                                            label={productSizes.length > 1 ? dataItem.code : 'Quantity:'}
                                            properties={{
                                                value: _.find(formValues.sizes, { id: dataItem.id })?.quantity || 0,
                                                step: 1,
                                                upHandler: <PlusOutlined />,
                                                downHandler: <MinusOutlined />,
                                                min: 0,
                                                onChange: (value: number) => {
                                                    _handleSizesChange(dataItem, value);
                                                },
                                                onStep: (value: number, info: { offset: number, type: 'up' | 'down' }) => {
                                                    _handleSizesChange(dataItem, value);
                                                }
                                            }}
                                        />

                                        {productSizes.length > 1 &&
                                            <React.Fragment>
                                                <span className="size-control-price">
                                                    {productSizes[0].priceNet && productSizes[0].priceNet.amount !== -1
                                                        ? dataItem.priceNet && productSizes[0].priceNet.amount !== dataItem.priceNet.amount && `${formatCurrency(dataItem.priceNet.amount, dataItem.priceNet.currency)} cad`
                                                        : '-'
                                                    }
                                                </span>
                                                <span className="size-control-code">
                                                    {`SapCode: ${dataItem.sapCode}`}
                                                </span>
                                            </React.Fragment>
                                        }
                                    </Col>
                                ))}
                            </BVShimmer>
                        </Row>
                    </div>
                </div>
                {productSizes && productSizes.some(dataItem => _handleSizeHasPool(dataItem)) &&
                    <React.Fragment>
                        <Divider />
                        <div className="products-form-group">
                            <span className="heading">
                                {'Choose Limited Edition Numbers'}
                            </span>
                            {/* Full width and outside the sizes grid: a size column is a quarter of the
                                drawer, too narrow for a list of numbers with its tags. */}
                            <div className="products-form-container">
                                <Row gutter={[16, 16]}>
                                    {productSizes.filter(dataItem => _handleSizeHasPool(dataItem)).map((dataItem, dataIndex) => (
                                        <Col key={dataIndex} span={24}>
                                            <BVLimitedEditionSelect
                                                label={productSizes.length > 1
                                                    ? `Size ${dataItem.code} - sap code ${dataItem.sapCode}:`
                                                    : 'Limited Edition numbers:'
                                                }
                                                numbers={_handleNumbersForSize(dataItem)}
                                                value={_.find(formValues.sizes, { id: dataItem.id })?.limitedEditionCodes ?? []}
                                                quantity={_.find(formValues.sizes, { id: dataItem.id })?.quantity}
                                                loading={limitedEditionsLoading}
                                                onChange={(codes) => _handleCodesChange(dataItem, codes)}
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        </div>
                    </React.Fragment>
                }
                <Divider />
                <div className="products-form-group">
                    <span className="heading">
                        {'Choose Shipping Date'}
                    </span>
                    <div className="products-form-container">
                        {formValues.facility &&
                            <span className="shipping-label">
                                {`Created shipping for ${formValues.facility.name}`}
                            </span>
                        }

                        {_handleFacilityExistingDates()}

                        <BVDatePicker
                            infoMessage={_handleFacilityAvailableDate()}
                            properties={{
                                placeholder: 'Select a shipping date...',
                                defaultPickerValue: moment(availableDate),
                                disabled: availableDateLoading || facilitiesLoading || !formValues.facility,
                                disabledDate: (current: any) => current && current < moment(availableDate).subtract(1, 'days').endOf('day'),
                                showToday: false,
                                format: momentFormat,
                                loading: availableDateLoading || facilitiesLoading,
                                value: formValues.deliveryDate,
                                onChange: (date: any, dateString: string) => handleChange('deliveryDate', date)
                            }}
                        />
                    </div>
                </div>
            </div>
        </Drawer>
    );
}

export default WideFormDrawer;